import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeftRight, MapPin, Search } from 'lucide-react'
import { useStoragePlaces } from '@/hooks/useStoragePlaces'
import { useFacilityLocations, useSetEquipmentRoom } from '@/hooks/useFacility'
import { PLACE_SEPARATOR, allRooms } from '@/lib/facilityLocation'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { PlacePicker } from '@/components/equipment/facility/PlacePicker'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { usePermission } from '@/lib/permissions'
import { FEATURES } from '@/lib/featureFlags'
import { useFeatureFlag } from '@/providers/FeatureFlagsProvider'
import { isForbidden, NO_INVENTORY_ACCESS } from '@/lib/storageUnitView'
import { useCapacitySummary, useStorageConditions, useStorageRoots } from '@/hooks/useStorage'
import { useStorageZones } from '@/hooks/usePickLists'
import { useProfiles } from '@/hooks/useInventoryMaster'
import { keptAtOptions } from '@/lib/keptAt'
import {
  buildStorageRows,
  familyCounts,
  filterStorageRows,
  rowActions,
  spaceText,
  storageListStats,
  unitStatus,
  type StorageListFilter,
  type StorageListRow,
} from '@/lib/storageList'
import { fillTone } from '@/lib/slotMetrics'
import { cn } from '@/lib/utils'
import { storageTemperaturesHref } from '@/lib/storageRoutes'
import { ReceiveStockButton } from '@/components/inventory/storage/ReceiveStockButton'
import { AddStorageUnitDialog } from '@/components/equipment/storage/AddStorageUnitDialog'
import { TEMPERATURE_FAMILIES, type TemperatureFamily } from '@/types/storage'
import { KeptAtCell } from './KeptAtCell'
import { ZoneCell } from './ZoneCell'
import { RowMenu, type RowMenuItem } from './RowMenu'

const TONE_BAR = {
  empty: 'bg-muted-foreground/20',
  some: 'bg-emerald-400',
  most: 'bg-amber-400',
  full: 'bg-rose-400',
} as const

const STORAGE_LIST_TABLE_ID = 'storage-list'

const STORAGE_LIST_COLUMNS: TableColumnDef[] = [
  { id: 'unit', header: 'Unit', locked: true, defaultWidth: 220, sortable: true },
  // Where the unit stands (z8nrz7fw25): Facility › Building › Floor › Room.
  { id: 'location', header: 'Location', defaultWidth: 260, sortable: true },
  { id: 'keptAt', header: 'Kept at', defaultWidth: 200, sortable: true },
  { id: 'zone', header: 'Zone', defaultWidth: 140, sortable: true },
  { id: 'space', header: 'Space', defaultWidth: 220, sortable: true },
  { id: 'status', header: 'Status', defaultWidth: 140, sortable: true },
  { id: 'actions', header: 'Actions', locked: true, defaultWidth: 120 },
]

const STORAGE_LIST_HEAD_CLASSES: Record<string, string | undefined> = {
  actions: 'w-28',
}

const FAMILY_LABEL: Record<TemperatureFamily, string> = {
  CRYOGENIC: 'LN2 vapor',
  ULTRA_LOW: 'Ultra-low',
  FREEZER: 'Freezer',
  REFRIGERATED: 'Refrigerated',
  AMBIENT_CRT: 'Room temp',
}

const CHIP_BASE =
  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors'
const CHIP_ACTIVE = 'border-transparent bg-primary text-primary-foreground'
const CHIP_IDLE = 'border-border bg-background text-muted-foreground hover:bg-muted'

const NO_FILTER: StorageListFilter = { q: '', family: 'ALL', attention: false, quick: null }

/**
 * The one storage list (design note §1), rendered by both Inventory →
 * Storage and Equipment → Storage so the two tabs show the same thing.
 *
 * `onCopy`/`onReplace`/`onDelete` are left to the host tab: each opens a
 * dialog that already exists (Copy layout, Replace unit, Delete unit) and
 * decides where to navigate once it is done, which differs by nothing here —
 * only whether the caller offers the action at all differs, and that is
 * already handled by `rowActions` plus these being optional.
 *
 * `receiveStock` is off on Equipment, which is for configuration: receiving
 * stock is Inventory's job, and its unit page there never shows stock either.
 */
export function StorageList({
  onCopy,
  onReplace,
  onDelete,
  unitHref,
  receiveStock = true,
}: {
  onCopy?: (row: StorageListRow) => void
  onReplace?: (row: StorageListRow) => void
  onDelete?: (row: StorageListRow) => void
  unitHref: (row: StorageListRow) => string
  /** Offer "Receive stock" in the header. */
  receiveStock?: boolean
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { can } = usePermission()
  const configure = can('inventory.configure')
  const manage = can('inventory.manage')
  const replace = can('storage.replace')
  const inventoryParts = useFeatureFlag(FEATURES.inventory) && can('inventory.view')

  const summary = useCapacitySummary()
  const roots = useStorageRoots()
  // Every zone, retired ones too, so a unit still on one shows its name; only
  // active ones are offered in the Zone menu.
  const zones = useStorageZones()
  const profiles = useProfiles('storage')
  const conditions = useStorageConditions()

  const [filter, setFilter] = useState<StorageListFilter>(NO_FILTER)
  const [addOpen, setAddOpen] = useState(false)

  const rows = useMemo(
    () => buildStorageRows(summary.data ?? [], roots.data ?? [], zones.data ?? []),
    [summary.data, roots.data, zones.data],
  )
  const stats = useMemo(() => storageListStats(rows), [rows])
  const counts = useMemo(() => familyCounts(rows), [rows])
  const attentionCount = useMemo(() => rows.filter((r) => r.needsAttention).length, [rows])
  const filtered = useMemo(() => filterStorageRows(rows, filter), [rows, filter])

  const keptAtChoices = useMemo(
    () => keptAtOptions(profiles.data ?? [], conditions.data?.conditions ?? []),
    [profiles.data, conditions.data],
  )
  const zoneChoices = useMemo(
    () => (zones.data ?? []).filter((z) => z.active).map((z) => ({ id: z.id, name: z.name })),
    [zones.data],
  )

  const places = useStoragePlaces()
  // Each unit's current room, so the Location cell's picker opens on it.
  const canPlace = can('equipment.manage')
  const locations = useFacilityLocations(canPlace)
  const roomOfUnit = useMemo(() => {
    const out = new Map<string, string>()
    for (const r of locations.data ? allRooms(locations.data) : []) for (const u of r.units) out.set(u.id, r.id)
    return out
  }, [locations.data])
  const cols = useTableColumns(STORAGE_LIST_TABLE_ID, STORAGE_LIST_COLUMNS)
  const sortAccessors = useMemo<SortAccessors<StorageListRow>>(
    () => ({
      unit: (r) => r.code,
      location: (r) => places.get(r.code) ?? null,
      keptAt: (r) =>
        r.condition ? (conditions.data?.conditions.find((c) => c.name === r.condition)?.label ?? r.condition) : null,
      zone: (r) => r.zoneName,
      space: (r) => (r.notSetUp ? null : r.spaces === 0 ? 0 : r.used / r.spaces),
      status: (r) => unitStatus(r).label,
    }),
    [conditions.data, places],
  )
  const sortedRows = useSortedRows(filtered, cols.sort, sortAccessors)

  const rootedEquipmentIds = useMemo(() => new Set((roots.data ?? []).map((r) => r.equipmentId)), [roots.data])

  function openUnit(row: StorageListRow) {
    navigate(unitHref(row), { state: { from: location.pathname + location.search } })
  }

  function menuItemsFor(row: StorageListRow): RowMenuItem[] {
    const items: RowMenuItem[] = []
    for (const action of rowActions(row, { configure, replace })) {
      switch (action) {
        case 'open':
          items.push({ key: 'open', label: 'Open unit', onSelect: () => openUnit(row) })
          break
        case 'settings':
          items.push({
            key: 'settings',
            label: 'Edit settings',
            onSelect: () =>
              navigate(`${unitHref(row)}?panel=settings`, { state: { from: location.pathname + location.search } }),
          })
          break
        case 'copy':
          if (onCopy) items.push({ key: 'copy', label: 'Copy layout to new unit', onSelect: () => onCopy(row) })
          break
        case 'replace':
          if (onReplace) items.push({ key: 'replace', label: 'Replace unit…', onSelect: () => onReplace(row) })
          break
        case 'delete':
          if (onDelete) items.push({ key: 'delete', label: 'Delete unit…', onSelect: () => onDelete(row), danger: true })
          break
      }
    }
    return items
  }

  if (roots.isLoading || summary.isLoading) {
    return <p className='text-sm text-muted-foreground'>Loading storage…</p>
  }
  const loadError = roots.error ?? summary.error
  if (loadError) {
    // Both reads are inventory routes: refused, it is access, not a fault.
    if (!inventoryParts || isForbidden(loadError)) {
      return <p className='text-sm text-muted-foreground'>{NO_INVENTORY_ACCESS}</p>
    }
    return <p className='text-sm text-destructive'>Could not load storage: {loadError.message}</p>
  }

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h2 className='text-lg font-semibold text-gray-900'>Storage</h2>
          <p className='text-sm text-muted-foreground'>Every freezer, fridge and shelf, and how full it is.</p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          {receiveStock && manage && <ReceiveStockButton />}
          {replace && (
            <Button
              size='sm'
              variant='outline'
              className='gap-1.5'
              onClick={() => navigate('/inventory/storage-transfers')}
            >
              <ArrowLeftRight className='h-4 w-4' />
              Transfer plans
            </Button>
          )}
          <Button size='sm' variant='outline' onClick={() => navigate(storageTemperaturesHref())}>
            ❄ Temperatures
          </Button>
          {configure && (
            <Button size='sm' onClick={() => setAddOpen(true)}>
              ＋ Add storage unit
            </Button>
          )}
        </div>
      </div>

      <div className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
        <SummaryCard label='Storage units' value={stats.units} />
        <SummaryCard label='Free spaces across all units' value={stats.free} />
        <SummaryCard
          label={stats.full === 1 ? 'Unit full' : 'Units full'}
          value={stats.full}
          alert={stats.full > 0}
          active={filter.quick === 'full'}
          onToggle={
            stats.full > 0 ? () => setFilter((f) => ({ ...f, quick: f.quick === 'full' ? null : 'full' })) : undefined
          }
        />
        <SummaryCard
          label={stats.notSetUp === 1 ? 'Unit not set up yet' : 'Units not set up yet'}
          value={stats.notSetUp}
          alert={stats.notSetUp > 0}
          active={filter.quick === 'notSetUp'}
          onToggle={
            stats.notSetUp > 0
              ? () => setFilter((f) => ({ ...f, quick: f.quick === 'notSetUp' ? null : 'notSetUp' }))
              : undefined
          }
        />
      </div>

      <div className='flex flex-wrap items-center gap-2'>
        <div className='relative w-full max-w-[280px]'>
          <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            value={filter.q}
            onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            placeholder='Search units by code or name'
            className='pl-9'
          />
        </div>
        <button
          type='button'
          className={cn(CHIP_BASE, filter.family === 'ALL' ? CHIP_ACTIVE : CHIP_IDLE)}
          onClick={() => setFilter((f) => ({ ...f, family: 'ALL' }))}
        >
          All <span className='rounded-full bg-black/10 px-1.5 text-[10px]'>{rows.length}</span>
        </button>
        {TEMPERATURE_FAMILIES.filter((fam) => (counts[fam] ?? 0) > 0).map((fam) => (
          <button
            key={fam}
            type='button'
            className={cn(CHIP_BASE, filter.family === fam ? CHIP_ACTIVE : CHIP_IDLE)}
            onClick={() => setFilter((f) => ({ ...f, family: f.family === fam ? 'ALL' : fam }))}
          >
            {FAMILY_LABEL[fam]} <span className='rounded-full bg-black/10 px-1.5 text-[10px]'>{counts[fam]}</span>
          </button>
        ))}
        <span className='flex-1' />
        <button
          type='button'
          className={cn(CHIP_BASE, filter.attention ? CHIP_ACTIVE : CHIP_IDLE)}
          onClick={() => setFilter((f) => ({ ...f, attention: !f.attention }))}
        >
          Needs attention <span className='rounded-full bg-black/10 px-1.5 text-[10px]'>{attentionCount}</span>
        </button>
      </div>

      {sortedRows.length === 0 ? (
        <Card>
          <CardContent className='py-10 text-center text-sm text-muted-foreground'>
            {rows.length === 0 ? (
              <>
                No storage units yet. Shelves are set up under{' '}
                <span className='font-medium'>Equipment → Storage</span>, on a unit whose type is{' '}
                <span className='font-medium'>Purpose = Storage</span>.
              </>
            ) : (
              'No unit matches these filters.'
            )}
          </CardContent>
        </Card>
      ) : (
        <div className='overflow-hidden rounded-lg border'>
          <Table>
            <TableHeader>
              <TableRow className='hover:bg-transparent'>
                {cols.visible.map(({ def }) => (
                  <ResizableTableHead
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    className={STORAGE_LIST_HEAD_CLASSES[def.id]}
                    resizable={def.id !== 'actions'}
                  >
                    {def.header}
                  </ResizableTableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map((row) => {
                const status = unitStatus(row)
                const pct = row.spaces === 0 ? 0 : Math.round((row.used / row.spaces) * 100)

                return (
                  <TableRow key={row.nodeId} className='cursor-pointer' onClick={() => openUnit(row)}>
                    <ColumnCells
                      columns={cols}
                      cells={{
                        unit: (
                          <TableCell>
                            <div className='font-medium'>{row.code}</div>
                            {row.name && <div className='text-xs text-muted-foreground'>{row.name}</div>}
                          </TableCell>
                        ),
                        location: (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <LocationCell
                              place={places.get(row.code) ?? null}
                              equipmentId={row.equipmentId}
                              roomId={row.equipmentId ? (roomOfUnit.get(row.equipmentId) ?? null) : null}
                              editable={canPlace}
                            />
                          </TableCell>
                        ),
                        keptAt: (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <KeptAtCell row={row} options={keptAtChoices} readOnly={!configure} />
                          </TableCell>
                        ),
                        zone: (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <ZoneCell row={row} zones={zoneChoices} readOnly={!configure} />
                          </TableCell>
                        ),
                        space: (
                          <TableCell>
                            {row.notSetUp ? (
                              configure ? (
                                <Link
                                  to={`${unitHref(row)}?setup=1`}
                                  onClick={(e) => e.stopPropagation()}
                                  className='inline-flex items-center gap-1 text-sm font-medium text-amber-700'
                                >
                                  ⚠ Not set up · <span className='underline'>Set up now</span>
                                </Link>
                              ) : (
                                <span className='text-sm text-amber-700'>⚠ Not set up</span>
                              )
                            ) : (
                              <div className='space-y-1'>
                                <div className='h-1.5 w-32 overflow-hidden rounded-full bg-muted'>
                                  <div
                                    className={cn('h-full rounded-full', TONE_BAR[fillTone(pct)])}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <div className='text-xs text-muted-foreground'>{spaceText(row)}</div>
                              </div>
                            )}
                          </TableCell>
                        ),
                        status: (
                          <TableCell>
                            <span className='inline-flex items-center gap-1.5 text-sm'>
                              <span
                                className={cn(
                                  'h-1.5 w-1.5 rounded-full',
                                  status.tone === 'ok'
                                    ? 'bg-emerald-500'
                                    : status.tone === 'warn'
                                      ? 'bg-amber-500'
                                      : 'bg-rose-500',
                                )}
                              />
                              {status.label}
                            </span>
                          </TableCell>
                        ),
                        actions: (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <div className='flex items-center justify-end gap-1'>
                              <Button size='sm' variant='outline' className='h-7' onClick={() => openUnit(row)}>
                                Open
                              </Button>
                              <RowMenu items={menuItemsFor(row)} />
                            </div>
                          </TableCell>
                        ),
                      }}
                    />
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <AddStorageUnitDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        rootedEquipmentIds={rootedEquipmentIds}
        onAdded={(id) => {
          setAddOpen(false)
          // `unitHref` is typed over a full row, but only `nodeId` is ever read
          // from it — there is no row yet for a unit that was just created.
          navigate(`${unitHref({ nodeId: id } as StorageListRow)}?setup=1`)
        }}
      />
    </div>
  )
}

/** The room in bold, and the facility, building and floor it is in under it. */
/**
 * The room in bold, and the facility, building and floor it is in under it.
 * With `equipment.manage` the cell opens the site picker, and choosing a room
 * saves it at once — the same in-place editing as Kept at and Zone.
 */
function LocationCell({
  place,
  equipmentId,
  roomId,
  editable,
}: {
  place: string | null
  equipmentId: string | null
  roomId: string | null
  editable: boolean
}) {
  const [open, setOpen] = useState(false)
  const setRoom = useSetEquipmentRoom()
  const parts = place ? place.split(PLACE_SEPARATOR) : []
  const shown = !place ? (
    <span className='text-xs text-muted-foreground'>{editable ? 'Set room' : 'Not in a room yet'}</span>
  ) : (
    <span className='block min-w-0' title={place}>
      <span className='flex items-center gap-1 text-sm font-medium'>
        <MapPin className='h-3.5 w-3.5 shrink-0 text-primary' />
        <span className='truncate'>{parts[parts.length - 1]}</span>
      </span>
      {parts.length > 1 && (
        <span className='block truncate pl-[18px] text-xs text-muted-foreground'>{parts.slice(0, -1).join(PLACE_SEPARATOR)}</span>
      )}
    </span>
  )
  if (!editable || !equipmentId) return shown

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) setRoom.reset()
      }}
    >
      <PopoverTrigger asChild>
        <button
          type='button'
          className='-mx-1.5 w-full rounded-md px-1.5 py-1 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
          aria-label={place ? `Change room, now ${place}` : 'Set room'}
        >
          {shown}
        </button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-[min(760px,92vw)] space-y-2'>
        <p className='text-sm font-medium'>Which room is it in?</p>
        <PlacePicker
          value={roomId}
          disabled={setRoom.isPending}
          onChange={(next) => setRoom.mutate({ equipmentId, roomId: next }, { onSuccess: () => setOpen(false) })}
        />
        {setRoom.error && <p className='text-xs text-destructive'>{setRoom.error.message}</p>}
      </PopoverContent>
    </Popover>
  )
}

function SummaryCard({
  label,
  value,
  alert,
  active,
  onToggle,
}: {
  label: string
  value: number
  alert?: boolean
  active?: boolean
  onToggle?: () => void
}) {
  const body = (
    <Card className={cn(alert && 'border-amber-300 bg-amber-50/60', active && 'ring-2 ring-primary')}>
      <CardContent className='p-4'>
        <div className={cn('text-2xl font-semibold', alert && 'text-amber-700')}>{value}</div>
        <div className='text-xs text-muted-foreground'>{label}</div>
      </CardContent>
    </Card>
  )
  if (!onToggle) return body
  return (
    <button type='button' onClick={onToggle} className='text-left' aria-pressed={active}>
      {body}
    </button>
  )
}
